import React from 'react';
import { AppStoreNotificationsPanel } from '../../components/AppStoreNotificationsPanel';
import { SubscriptionStatusTable } from '../../components/SubscriptionStatusTable';
import { MaintenanceManagementPanel } from '../../components/MaintenanceManagementPanel';
import { AdminLog } from './useWaitlistOperations';

interface OperationsPanelProps {
  appPublished: boolean;
  appPublishedLoading: boolean;
  onRequestToggleApp: () => void;
  androidPublished: boolean;
  androidPublishedLoading: boolean;
  onRequestToggleAndroid: () => void;
  logs: AdminLog[];
  logsLoading: boolean;
  showLogsInfo: boolean;
  onToggleLogsInfo: () => void;
  onRefreshLogs: () => void;
}

const OperationsPanel: React.FC<OperationsPanelProps> = ({
  appPublished,
  appPublishedLoading,
  onRequestToggleApp,
  androidPublished,
  androidPublishedLoading,
  onRequestToggleAndroid,
  logs,
  logsLoading,
  showLogsInfo,
  onToggleLogsInfo,
  onRefreshLogs,
}) => (
  <>
    {/* System Status & Maintenance — most actionable, top of dashboard */}
    <MaintenanceManagementPanel />

    {/* App Store Toggle */}
    <div className="rp-bento-card mb-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>App Store Status</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
            {appPublished
              ? 'App Store links are LIVE — buttons link directly to the App Store.'
              : 'Coming Soon mode — buttons show a "Coming Soon" toast instead.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
            appPublished ? 'bg-green-500/20 text-green-400 border border-green-400/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-400/30'
          }`}>
            {appPublished ? 'LIVE' : 'COMING SOON'}
          </span>
          <button
            onClick={onRequestToggleApp}
            disabled={appPublishedLoading}
            aria-label="Toggle App Store live links"
            className="admin-pubtoggle"
          >
            <span
              className="admin-pubtoggle-track"
              style={{ backgroundColor: appPublished ? 'var(--admin-success)' : 'var(--admin-neutral-track)' }}
            >
              <span
                className="admin-pubtoggle-knob"
                style={{ transform: appPublished ? 'translateX(28px)' : 'translateX(0)' }}
              />
            </span>
          </button>
        </div>
      </div>
    </div>

    {/* Google Play Toggle — independent of the App Store toggle above */}
    <div className="rp-bento-card mb-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>Google Play Status</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
            {androidPublished
              ? 'Google Play links are LIVE — buttons link directly to the Play Store listing.'
              : 'Coming Soon mode — buttons show an Android "Coming Soon" toast and never link externally.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
            androidPublished ? 'bg-green-500/20 text-green-400 border border-green-400/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-400/30'
          }`}>
            {androidPublished ? 'LIVE' : 'COMING SOON'}
          </span>
          <button
            onClick={onRequestToggleAndroid}
            disabled={androidPublishedLoading}
            aria-label="Toggle Google Play live links"
            className="admin-pubtoggle"
          >
            <span
              className="admin-pubtoggle-track"
              style={{ backgroundColor: androidPublished ? 'var(--admin-success)' : 'var(--admin-neutral-track)' }}
            >
              <span
                className="admin-pubtoggle-knob"
                style={{ transform: androidPublished ? 'translateX(28px)' : 'translateX(0)' }}
              />
            </span>
          </button>
        </div>
      </div>
    </div>

    {/* System Logs */}
    <div className="rp-bento-card mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>System Logs</h2>
          <div className="relative">
            <button
              onClick={onToggleLogsInfo}
              aria-label="What do system logs show?"
              className="w-11 h-11 rounded-full bg-blue-500/20 border border-blue-400/30 inline-flex items-center justify-center text-blue-400 hover:bg-blue-500/30 transition-colors text-xs font-bold"
            >
              i
            </button>
            {showLogsInfo && (
              <div className="absolute top-6 left-0 z-10 w-80 p-3 rounded-lg border border-white/20 text-sm" style={{ backgroundColor: 'var(--rp-surface)', color: 'var(--rp-text)' }}>
                <div className="mb-2 font-semibold">What System Logs Show:</div>
                <ul className="space-y-1 text-xs" style={{ color: 'var(--rp-text-secondary)' }}>
                  <li>Admin actions: waitlist deletions, modifications</li>
                  <li>Application events: user signups, email sends</li>
                  <li>Database operations: successful/failed queries</li>
                  <li>Security events: authentication attempts</li>
                </ul>
                <div className="mt-2 pt-2 border-t border-white/10 text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>
                  Note: Dev errors appear in browser console, not here
                </div>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onRefreshLogs}
          disabled={logsLoading}
          className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
          style={{ color: 'var(--rp-text)' }}
        >
          {logsLoading ? 'Loading...' : 'Refresh Logs'}
        </button>
      </div>
      <div
        className="bg-black/50 rounded-lg p-4 font-mono text-sm max-h-64 overflow-y-auto border border-white/10"
        style={{ backgroundColor: 'var(--rp-surface-secondary)' }}
      >
        {logs.length === 0 ? (
          <div className="text-center py-4" style={{ color: 'var(--rp-text-tertiary)' }}>No logs available</div>
        ) : (
          <div className="space-y-1">
            {logs.map((log) => (
              <div key={log.id} className="flex gap-3 text-xs">
                <span style={{ color: 'var(--rp-text-tertiary)' }} className="w-36 flex-shrink-0 break-words min-w-0">
                  {log.log_timestamp ? new Date(log.log_timestamp).toLocaleString() : ''}
                </span>
                <span className={`w-12 flex-shrink-0 font-semibold ${
                  log.level === 'error' ? 'text-red-400' : log.level === 'warn' ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  {log.level.toUpperCase()}
                </span>
                <span style={{ color: 'var(--rp-text-secondary)' }} className="flex-1 min-w-0 break-words">{log.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* App Store Notifications */}
    <div className="mb-6">
      <AppStoreNotificationsPanel />
    </div>

    {/* Subscription Status */}
    <div className="mb-6">
      <SubscriptionStatusTable />
    </div>
  </>
);

export default OperationsPanel;
